import React, { useState, useEffect, useContext } from "react";
import { useParams, Link } from "react-router-dom";
import { Container } from "reactstrap";
import Chart from "react-apexcharts";
import { BreadcrumbContext } from "../../../Layouts/Header";
import CommonTabHeader from "../../../Components/Common/CommonTabHeader";
import { STUDY_PATHS } from "Routes/paths";
import styles from "../css/singlestudydashboard.module.scss";
import Commonbadge from "Components/Common/Commonbadge";
import skeletonStyles from "../../../assets/scss/common/viewSkeleton.module.scss";
import { IconCalendar } from "modules/Volunteer/MasterVolunteer/helper/volunteer.constant";
import { IconBuilding, IconCheckSquare, IconDoorOpen, IconExclamation, IconFlask, IconRibbon, IconSyringe, IconTag, IconThreePeople, IconUser, IconUserCheck, IconUserPlus, IconUsersCheck, renderSkeleton } from "../helper/dashboard.constant";

/* --------------------------------------------------------------------------
   CUSTOM INLINE SVG ICONS
   -------------------------------------------------------------------------- */



const SingleStudyDashboard: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const studyCode = id || "STUDY-BA-001";
  document.title = `${studyCode} Dashboard | ZENTIXS VISTA`;

  const breadcrumbContext = useContext(BreadcrumbContext);
  const setBreadcrumbItems = breadcrumbContext?.setBreadcrumbItems;
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 600);
    return () => clearTimeout(timer);
  }, []);



  // Sync breadcrumbs
  useEffect(() => {
    const defaultBreadcrumbs = [
      { text: "Home", link: "/" },
      { text: "Studies", link: STUDY_PATHS.LIST },
      { text: `${studyCode} Dashboard` },
    ];
    setBreadcrumbItems?.(defaultBreadcrumbs);

    return () => {
      setBreadcrumbItems?.([]);
    };
  }, [setBreadcrumbItems, studyCode]);

  /* ----------------------------------------------------
     ApexCharts Configurations (Mock study-specific metrics)
     ---------------------------------------------------- */

  // 1. Subject Status Overview Donut Chart
  const donutOptions: ApexCharts.ApexOptions = {
    chart: {
      type: "donut",
    },
    labels: [
      "Screened",
      "Randomized",
      "Dosed (P1)",
      "Dosed (P2)",
      "Completed",
      "Dropouts",
    ],
    colors: ["#a855f7", "#22c55e", "#3b82f6", "#f59e0b", "#14b8a6", "#ef4444"],
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    },
    plotOptions: {
      pie: {
        donut: {
          size: "74%",
          labels: {
            show: true,
            total: {
              show: true,
              label: "Total",
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "#64748b",
              formatter: () => "180",
            },
            value: {
              show: true,
              fontSize: "1.25rem",
              fontWeight: 800,
              color: "#0f172a",
              offsetY: -2,
            },
          },
        },
      },
    },
  };

  const donutSeries = [200, 180, 150, 130, 120, 12];



  return (
    <div style={{ overflowX: "hidden", minHeight: "100vh", backgroundColor: "#f4f7fa" }}>
      {/* Page Header */}
      <div style={{ padding: "0 12px", backgroundColor: "#fff" }}>
        <CommonTabHeader title="Study Dashboard" />
      </div>

      <div className={styles.dashboardWrapper}>
        <div className={styles.dashboardHeader}>
          <div className={styles.headerTitleSection}>
            {isLoading ? (
              <div className={skeletonStyles.skeleton} style={{ width: "240px", height: "14px", borderRadius: "4px" }} />
            ) : (
              <span style={{ fontSize: "0.8125rem", color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Study Performance & Management Dashboard
              </span>
            )}
          </div>
          <div className={styles.headerActions}>
            {isLoading ? (
              <>
                <div className={skeletonStyles.skeleton} style={{ width: "150px", height: "30px", borderRadius: "9999px" }} />
                <div className={skeletonStyles.skeleton} style={{ width: "140px", height: "30px", borderRadius: "9999px" }} />
              </>
            ) : (
              <>
                <div className={styles.timeBadge}>
                  <IconCalendar />
                  <span>16 Jun 2026, 10:30 AM</span>
                </div>
                <div className={styles.userProfile}>
                  <div className={styles.avatar}>SC</div>
                  <span className={styles.role}>Study Coordinator</span>
                </div>
              </>
            )}
          </div>
        </div>

        <Container fluid className="p-0">
          {isLoading ? renderSkeleton(styles, skeletonStyles) : (
            <>
              {/* ================= TOP SECTION (Left Info + 5 Stats Cards) ================= */}
              <div className={styles.topRowGrid}>
                {/* Left Study Profile Card */}
                <div className={styles.studyProfileCard}>
                  <div className={styles.leftSection}>
                    <div className={styles.iconBadge}>
                      <IconThreePeople />
                    </div>
                    <div className={styles.studyIdentity}>
                      <span className={styles.codeLabel}>Study Code</span>
                      <h4 className={styles.codeValue}>{studyCode}</h4>
                      <span className={styles.studyName}>
                        Bioavailability Study of Test Product (Fasting)
                      </span>
                    </div>
                  </div>

                  <div className={styles.divider}></div>

                  <div className={styles.rightSection}>
                    <table className={styles.detailsTable}>
                      <tbody>
                        <tr>
                          <td className={styles.label}>Study Type</td>
                          <td className={styles.colon}>:</td>
                          <td className={styles.value}>BA/BE</td>
                        </tr>
                        <tr>
                          <td className={styles.label}>Study Design</td>
                          <td className={styles.colon}>:</td>
                          <td className={styles.value}>
                            Randomized, 2 Period, 2 Sequence, Crossover
                          </td>
                        </tr>
                        <tr>
                          <td className={styles.label}>Start Date</td>
                          <td className={styles.colon}>:</td>
                          <td className={styles.value}>01 Jun 2026</td>
                        </tr>
                        <tr>
                          <td className={styles.label}>End Date</td>
                          <td className={styles.colon}>:</td>
                          <td className={styles.value}>31 Aug 2026</td>
                        </tr>
                        <tr>
                          <td className={styles.label}>Status</td>
                          <td className={styles.colon}>:</td>
                          <td className={styles.value}>
                            <Commonbadge status="Ongoing" style={{ width: "85px" }} />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Right 5 Stats Cards Grid */}
                <div className={styles.statsSubGrid}>
                  {/* Stat 1 */}
                  <div className={styles.statItemCard}>
                    <div className={styles.cardTop}>
                      <div className={`${styles.iconBadge} ${styles.purple}`}>
                        <IconUser />
                      </div>
                      <span className={styles.label}>Total Subjects</span>
                    </div>
                    <span className={styles.value}>180</span>
                    <Link to="#" className={styles.viewAllLink} onClick={() => alert("Total Subjects Detail")}>
                      View all
                    </Link>
                  </div>

                  {/* Stat 2 */}
                  <div className={styles.statItemCard}>
                    <div className={styles.cardTop}>
                      <div className={`${styles.iconBadge} ${styles.green}`}>
                        <IconCheckSquare />
                      </div>
                      <span className={styles.label}>Screened</span>
                    </div>
                    <span className={styles.value} style={{ color: "#10b981" }}>200</span>
                    <Link to="#" className={styles.viewAllLink} onClick={() => alert("Screened Detail")}>
                      View all
                    </Link>
                  </div>

                  {/* Stat 3 */}
                  <div className={styles.statItemCard}>
                    <div className={styles.cardTop}>
                      <div className={`${styles.iconBadge} ${styles.blue}`}>
                        <IconUserCheck />
                      </div>
                      <span className={styles.label}>Randomized</span>
                    </div>
                    <span className={styles.value} style={{ color: "#2563eb" }}>180</span>
                    <Link to="#" className={styles.viewAllLink} onClick={() => alert("Randomized Detail")}>
                      View all
                    </Link>
                  </div>

                  {/* Stat 4 */}
                  <div className={styles.statItemCard}>
                    <div className={styles.cardTop}>
                      <div className={`${styles.iconBadge} ${styles.orange}`}>
                        <IconRibbon />
                      </div>
                      <span className={styles.label}>Completed</span>
                    </div>
                    <span className={styles.value} style={{ color: "#d97706" }}>120</span>
                    <Link to="#" className={styles.viewAllLink} onClick={() => alert("Completed Detail")}>
                      View all
                    </Link>
                  </div>

                  {/* Stat 5 */}
                  <div className={styles.statItemCard}>
                    <div className={styles.cardTop}>
                      <div className={`${styles.iconBadge} ${styles.red}`}>
                        <IconExclamation />
                      </div>
                      <span className={styles.label}>Dropouts</span>
                    </div>
                    <span className={styles.value} style={{ color: "#dc2626" }}>12</span>
                    <Link to="#" className={styles.viewAllLink} onClick={() => alert("Dropouts Detail")}>
                      View all
                    </Link>
                  </div>
                </div>
              </div>

              {/* ================= MIDDLE ROW (7 Action Cards) ================= */}
              <div className={styles.middleGrid}>
                {/* Action 1 */}
                <div className={styles.actionCard}>
                  <div className={styles.cardTop}>
                    <div className={`${styles.iconBadge} ${styles.purple}`}>
                      <IconFlask />
                    </div>
                    <span className={styles.label}>Pharmacy</span>
                  </div>
                  <h5 className={styles.value}>3</h5>
                  <span className={styles.desc}>Batches Available</span>
                  <Link to="#" className={styles.viewDetails} onClick={() => alert("View Pharmacy Details")}>
                    View Details <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>

                {/* Action 2 */}
                <div className={styles.actionCard}>
                  <div className={styles.cardTop}>
                    <div className={`${styles.iconBadge} ${styles.cyan}`}>
                      <IconTag />
                    </div>
                    <span className={styles.label}>Label</span>
                  </div>
                  <h5 className={styles.value}>6</h5>
                  <span className={styles.desc}>Labels Generated</span>
                  <Link to="#" className={styles.viewDetails} onClick={() => alert("View Label Details")}>
                    View Details <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>

                {/* Action 3 */}
                <div className={styles.actionCard}>
                  <div className={styles.cardTop}>
                    <div className={`${styles.iconBadge} ${styles.green}`}>
                      <IconUserPlus />
                    </div>
                    <span className={styles.label}>Subject Assignment</span>
                  </div>
                  <h5 className={styles.value}>180</h5>
                  <span className={styles.desc}>Subjects Assigned</span>
                  <Link to="#" className={styles.viewDetails} onClick={() => alert("View Assignment Details")}>
                    View Details <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>

                {/* Action 4 */}
                <div className={styles.actionCard}>
                  <div className={styles.cardTop}>
                    <div className={`${styles.iconBadge} ${styles.orange}`}>
                      <IconDoorOpen />
                    </div>
                    <span className={styles.label}>Check In / Check Out</span>
                  </div>
                  <h5 className={styles.value}>45</h5>
                  <span className={styles.desc}>Today</span>
                  <Link to="#" className={styles.viewDetails} onClick={() => alert("View Check In/Out Details")}>
                    View Details <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>

                {/* Action 5 */}
                <div className={styles.actionCard}>
                  <div className={styles.cardTop}>
                    <div className={`${styles.iconBadge} ${styles.blue}`}>
                      <IconSyringe />
                    </div>
                    <span className={styles.label}>Dosing</span>
                  </div>
                  <h5 className={styles.value}>28</h5>
                  <span className={styles.desc}>Today</span>
                  <Link to="#" className={styles.viewDetails} onClick={() => alert("View Dosing Details")}>
                    View Details <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>

                {/* Action 6 */}
                <div className={styles.actionCard}>
                  <div className={styles.cardTop}>
                    <div className={`${styles.iconBadge} ${styles.cyan}`}>
                      <IconUsersCheck />
                    </div>
                    <span className={styles.label}>Subject Check In/Out</span>
                  </div>
                  <h5 className={styles.value}>86</h5>
                  <span className={styles.desc}>Today</span>
                  <Link to="#" className={styles.viewDetails} onClick={() => alert("View Subject Check In/Out Details")}>
                    View Details <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>

                {/* Action 7 */}
                <div className={styles.actionCard}>
                  <div className={styles.cardTop}>
                    <div className={`${styles.iconBadge} ${styles.purple}`}>
                      <IconBuilding />
                    </div>
                    <span className={styles.label}>Sponsor</span>
                  </div>
                  <h5 className={`${styles.value} ${styles.sponsorText}`} title="ABC Pharma Ltd.">ABC Pharma Ltd.</h5>
                  <span className={`${styles.desc} ${styles.activeStatus}`}>Active</span>
                  <Link to="#" className={styles.viewDetails} onClick={() => alert("View Sponsor Details")}>
                    View Details <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>

              {/* ================= DASHBOARD METRICS GRID (3 columns, 2 rows) ================= */}
              <div className={styles.dashboardGrid}>
                {/* Block 1: Subject Status Overview */}
                <div className={styles.premiumCard}>
                  <div className={styles.cardHeader}>
                    <span>Subject Status Overview</span>
                  </div>
                  <div className={styles.cardBody} style={{ minHeight: "220px", display: "flex", alignItems: "center" }}>
                    <div className={styles.donutChartWrapper}>
                      <div className={styles.chartContainer}>
                        <Chart options={donutOptions} series={donutSeries} type="donut" height={155} />
                      </div>
                      <div className={styles.donutLegend}>
                        <div className={styles.legendRow}>
                          <div className={styles.legendLabel}>
                            <span className={`${styles.bullet} ${styles.purple}`}></span>
                            <span>Screened</span>
                          </div>
                          <span className={styles.legendValue}>200 (40.0%)</span>
                        </div>
                        <div className={styles.legendRow}>
                          <div className={styles.legendLabel}>
                            <span className={`${styles.bullet} ${styles.green}`}></span>
                            <span>Randomized</span>
                          </div>
                          <span className={styles.legendValue}>180 (36.0%)</span>
                        </div>
                        <div className={styles.legendRow}>
                          <div className={styles.legendLabel}>
                            <span className={`${styles.bullet} ${styles.blue}`}></span>
                            <span>Dosed (P1)</span>
                          </div>
                          <span className={styles.legendValue}>150 (30.0%)</span>
                        </div>
                        <div className={styles.legendRow}>
                          <div className={styles.legendLabel}>
                            <span className={`${styles.bullet} ${styles.orange}`}></span>
                            <span>Dosed (P2)</span>
                          </div>
                          <span className={styles.legendValue}>130 (26.0%)</span>
                        </div>
                        <div className={styles.legendRow}>
                          <div className={styles.legendLabel}>
                            <span className={`${styles.bullet} ${styles.teal}`}></span>
                            <span>Completed</span>
                          </div>
                          <span className={styles.legendValue}>120 (24.0%)</span>
                        </div>
                        <div className={styles.legendRow}>
                          <div className={styles.legendLabel}>
                            <span className={`${styles.bullet} ${styles.red}`}></span>
                            <span>Dropouts</span>
                          </div>
                          <span className={styles.legendValue}>12 (2.0%)</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Block 2: Pharmacy - Batch Status */}
                <div className={styles.premiumCard}>
                  <div className={styles.cardHeader}>
                    <span>Pharmacy - Batch Status</span>
                  </div>
                  <div className={styles.cardBody} style={{ display: "flex", flexDirection: "column" }}>
                    <table className={styles.miniTable}>
                      <thead>
                        <tr>
                          <th>Batch No.</th>
                          <th>Product</th>
                          <th>Units</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>BATCH-001</td>
                          <td>Test Product</td>
                          <td>240</td>
                          <td><Commonbadge status="Available" style={{ width: "85px" }} /></td>
                        </tr>
                        <tr>
                          <td>BATCH-002</td>
                          <td>Test Product</td>
                          <td>180</td>
                          <td><Commonbadge status="Available" style={{ width: "85px" }} /></td>
                        </tr>
                        <tr>
                          <td>BATCH-003</td>
                          <td>Reference Product</td>
                          <td>200</td>
                          <td><Commonbadge status="Available" style={{ width: "85px" }} /></td>
                        </tr>
                      </tbody>
                    </table>
                    <div className={styles.viewAllFooter}>
                      <Link to="#" className={styles.viewLink} onClick={() => alert("View All Batch Statuses")}>
                        View All
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Block 3: Label Summary */}
                <div className={styles.premiumCard}>
                  <div className={styles.cardHeader}>
                    <span>Label Summary</span>
                  </div>
                  <div className={styles.cardBody} style={{ display: "flex", flexDirection: "column" }}>
                    <table className={styles.miniTable}>
                      <thead>
                        <tr>
                          <th>Label Type</th>
                          <th>Gen.</th>
                          <th>Used</th>
                          <th>Rem.</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Bottle Label</td>
                          <td>300</td>
                          <td>180</td>
                          <td>120</td>
                        </tr>
                        <tr>
                          <td>Kit Label</td>
                          <td>180</td>
                          <td>120</td>
                          <td>60</td>
                        </tr>
                        <tr>
                          <td>Randomization Label</td>
                          <td>180</td>
                          <td>180</td>
                          <td>0</td>
                        </tr>
                      </tbody>
                    </table>
                    <div className={styles.viewAllFooter}>
                      <Link to="#" className={styles.viewLink} onClick={() => alert("View All Label Summary")}>
                        View All
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Block 4: Today's Check In / Check Out */}
                <div className={styles.premiumCard}>
                  <div className={styles.cardHeader}>
                    <span>Today's Check In / Check Out</span>
                  </div>
                  <div className={styles.cardBody} style={{ display: "flex", flexDirection: "column" }}>
                    <table className={styles.miniTable}>
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Subject Check In</td>
                          <td>28</td>
                        </tr>
                        <tr>
                          <td>Subject Check Out</td>
                          <td>24</td>
                        </tr>
                        <tr>
                          <td>Staff Check In</td>
                          <td>10</td>
                        </tr>
                        <tr>
                          <td>Staff Check Out</td>
                          <td>8</td>
                        </tr>
                      </tbody>
                    </table>
                    <div className={styles.viewAllFooter}>
                      <Link to="#" className={styles.viewLink} onClick={() => alert("View All Check In/Out Today")}>
                        View All
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Block 5: Today's Dosing Summary */}
                <div className={styles.premiumCard}>
                  <div className={styles.cardHeader}>
                    <span>Today's Dosing Summary</span>
                  </div>
                  <div className={styles.cardBody} style={{ display: "flex", flexDirection: "column" }}>
                    <table className={styles.miniTable}>
                      <thead>
                        <tr>
                          <th>Period</th>
                          <th>Planned</th>
                          <th>Compl.</th>
                          <th>Pend.</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Period 1</td>
                          <td>15</td>
                          <td>12</td>
                          <td>3</td>
                        </tr>
                        <tr>
                          <td>Period 2</td>
                          <td>13</td>
                          <td>10</td>
                          <td>3</td>
                        </tr>
                      </tbody>
                    </table>
                    <div className={styles.viewAllFooter}>
                      <Link to="#" className={styles.viewLink} onClick={() => alert("View All Today's Dosing")}>
                        View All
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Block 6: Sponsor Information */}
                <div className={styles.premiumCard}>
                  <div className={styles.cardHeader}>
                    <span>Sponsor Information</span>
                  </div>
                  <div className={styles.cardBody} style={{ display: "flex", flexDirection: "column" }}>
                    <div className={styles.sponsorDetails}>
                      <div className={styles.sponsorRow}>
                        <span className={styles.label}>Sponsor Name</span>
                        <span className={styles.value}>ABC Pharma Ltd.</span>
                      </div>
                      <div className={styles.sponsorRow}>
                        <span className={styles.label}>Contact Person</span>
                        <span className={styles.value}>Mr. Rajesh Sharma</span>
                      </div>
                      <div className={styles.sponsorRow}>
                        <span className={styles.label}>Email</span>
                        <span className={styles.value} style={{ fontSize: "0.6875rem" }}>rajesh.sharma@abcpharma.com</span>
                      </div>
                      <div className={styles.sponsorRow}>
                        <span className={styles.label}>Phone</span>
                        <span className={styles.value}>+91 98765 43210</span>
                      </div>
                      <div className={styles.sponsorRow}>
                        <span className={styles.label}>Status</span>
                        <span className={styles.activeStatus}>Active</span>
                      </div>
                    </div>
                    <div className={styles.viewAllFooter}>
                      <Link to="#" className={styles.viewLink} onClick={() => alert("View All Sponsor Details")}>
                        View All
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </Container>
      </div>
    </div>
  );
};

export default SingleStudyDashboard;
